import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import NextIcon from 'assets/actionPlan/next.svg';
import PrevIcon from 'assets/actionPlan/prev.svg';
import SaveIcon from 'assets/actionPlan/save.svg';
import Button from 'common-ui/button';
import { notifyError, notifySuccess } from 'utils/notifyConfig';

import {
  createAssignment,
  getDropdownActions,
} from '../../../features/actions/actionPlanActions';
import {
  selectAssignmentLoading,
  selectDropdownActionsList,
} from '../../../features/actions/actionPlanSlice';
import EmployeeSteps from '../employeeSteps';
import {
  AssignmentCard,
  CardDescription,
  CardTitle,
  Container,
  FooterContainer,
  Icon,
  Step,
  StepDivider,
  StepLabel,
  StepNumber,
  StepperContainer,
} from './ActionAssignment.styles';

const ActionAssignment = ({ onFinish }) => {
  const dispatch = useDispatch();
  const actionsList = useSelector(selectDropdownActionsList);
  const isSubmitting = useSelector(selectAssignmentLoading);
  const [selectedType, setSelectedType] = useState('employee');
  const [currentStep, setCurrentStep] = useState(1);
  const [isSaved, setIsSaved] = useState(false);
  const [isStepValid, setIsStepValid] = useState(false);
  const [stepData, setStepData] = useState(null);
  const [triggerValidation, setTriggerValidation] = useState(false);

  const stepsConfig = {
    employee: [
      { id: 1, label: 'Select Action' },
      { id: 2, label: 'Select Employees' },
      { id: 3, label: 'Configure KPI' },
      { id: 4, label: 'Compensation' },
    ],
    department: [
      { id: 1, label: 'Select Action' },
      { id: 2, label: 'Select Department' },
      { id: 3, label: 'Configure KPI' },
    ],
  };

  const steps = stepsConfig[selectedType];
  const isLastStep = currentStep === steps.length;

  const handleNext = () => {
    setTriggerValidation(Math.random());
  };
  const handleTypeChange = (type) => {
    setSelectedType(type);
    setCurrentStep(1);
    setIsSaved(false);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };
  const handleSaveAssignment = async () => {
    if (!stepData || isSubmitting) return;

    const isEmployee = selectedType === 'employee';

    const payload = {
      actionId: stepData.actionId,
      // effectiveDate: stepData.effectiveDate,
      effectiveDate: new Date(stepData.effectiveDate).toLocaleDateString('sv-SE'),
      actionType: stepData.actionType,
      estimationType: stepData.estimationType,
      kpiConfig: stepData.kpiConfig,
    };

    if (isEmployee) {
      const validEmployeeIds = (stepData.employeeIds || []).filter(
        (id) => id != null && id !== ''
      );
      payload.employeeIds = validEmployeeIds;
      payload.compensationConfig = stepData.compensationConfig;

      if (validEmployeeIds.length === 0) {
        console.error('ERROR: No valid employee IDs found!');
        notifyError('Please select at least one employee');
        return;
      }
    } else {
      payload.departmentIds = stepData.departmentIds;
      if (stepData.departmentIds?.length > 1) {
        console.warn(
          `Multiple departments selected (${stepData.departmentIds.length}), but backend only accepts one. Sending first department only.`
        );
      }
    }

    dispatch(
      createAssignment({
        assignmentData: payload,
        scope: isEmployee ? 'employee' : 'department',
      })
    ).then((res) => {
      if (!res.error) {
        setIsSaved(true);
        // notifySuccess('Assignment created successfully');
        onFinish?.();
      } else {
        console.error('Error Response:', res);
        notifyError(res.payload || 'Backend Validation Error');
      }
    });
  };

  useEffect(() => {
    if (triggerValidation === false) return;

    const timeout = setTimeout(() => {
      if (isStepValid) {
        if (currentStep < steps.length) {
          setCurrentStep((prev) => prev + 1);
        } else {
          handleSaveAssignment();
        }
      } else {
        // notifyError('Please fill all required fields');
      }

      setTriggerValidation(false);
    }, 0);

    return () => clearTimeout(timeout);
  }, [triggerValidation, isStepValid]);
  useEffect(() => {
    dispatch(getDropdownActions({ page: 0, size: 100, enabled: true }));
  }, [dispatch]);

  return (
    <>
      <Container>
        <AssignmentCard
          $active={selectedType === 'employee'}
          onClick={() => handleTypeChange('employee')}
        >
          <CardTitle $active={selectedType === 'employee'}>Employee Assignment</CardTitle>
          <CardDescription $active={selectedType === 'employee'}>
            Assign to individual employees with compensation
          </CardDescription>
        </AssignmentCard>

        <AssignmentCard
          $active={selectedType === 'department'}
          onClick={() => handleTypeChange('department')}
        >
          <CardTitle $active={selectedType === 'department'}>Department Assignment</CardTitle>
          <CardDescription $active={selectedType === 'department'}>
            Assign to department with aggregated KPI
          </CardDescription>
        </AssignmentCard>
      </Container>
      <StepperContainer>
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            <Step $active={currentStep === step.id} $completed={currentStep > step.id}>
              <StepNumber $active={currentStep === step.id}>{step.id}</StepNumber>
              <StepLabel $active={currentStep === step.id}>{step.label}</StepLabel>
            </Step>
            {index < steps.length - 1 && <StepDivider />}
          </React.Fragment>
        ))}
      </StepperContainer>
      <EmployeeSteps
        currentStep={currentStep}
        assignmentType={selectedType}
        isReadOnly={isSaved}
        setIsValid={setIsStepValid}
        onChange={setStepData}
        triggerValidation={triggerValidation}
      />

      <FooterContainer $isLastStep={isLastStep}>
        <Button
          outlined
          onClick={handleBack}
          icon={PrevIcon}
          disabled={currentStep === 1 || isSaved}
        >
          Back
        </Button>

        <Button
          primary
          onClick={handleNext}
          loading={isSubmitting}
          disabled={isSubmitting || isSaved}
        >
          {isLastStep ? 'Save Assignment' : 'Next'}
          <Icon src={isLastStep ? SaveIcon : NextIcon} $isLastStep={isLastStep} />
        </Button>
      </FooterContainer>
    </>
  );
};

export default ActionAssignment;
